import { useState, useEffect, useMemo } from 'react';
import { ArrowLeft, ChevronLeft, ChevronRight, Download, Loader2 } from 'lucide-react';
import { useAuth } from '../../auth';
import { useAPIStorage } from '../../hooks/useAPIStorage';
import { APIStorage } from '../../services/apiStorage';
import { DayEditModal } from '../Calendar/DayEditModal';
import { VacationRangeDialog } from '../Calendar/VacationRangeDialog';
import { MonthView } from '../Calendar/MonthView';
import { Legend } from '../Calendar/Legend';
import { VacationKPICards } from '../common/VacationKPICards';
import { buildCalendarMonths } from '../../hooks/useCalendarData';
import { calculateResults, recalcFutureVacationHours } from '../../utils/calculations';
import { exportDailyRangeCSV, parseISODate } from '../../utils/managerExport';
import type { CalendarDay } from '../../hooks/useCalendarData';
import type { AbsenceRequest, AbsenceType, DayEntry, WageType, CompanySettings, VacationCarryOver, SettingsSegment } from '../../types';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useAbsenceTypes } from '../../hooks/useAbsenceTypes';

interface Props {
  userId: string;
  userName: string;
  year: number;
  entries: Record<string, DayEntry>;
  onBack: () => void;
  onRefresh: () => void;
  /** Wenn true, kann HR alle Einträge bearbeiten (nicht nur Manager-Notes) */
  canEditEntries?: boolean;
  /** Callback wenn das Jahr gewechselt wird (nur für HR-Modus) */
  onYearChange?: (year: number) => void;
  /** Vorjahresdaten für 13-Wochen-Berechnung (bei Krankheit/Urlaub im Januar) */
  previousYearEntries?: Record<string, DayEntry>;
  /** WageType des betrachteten Mitarbeiters — bestimmt ob Betriebsferien klickbar/bearbeitbar sind. */
  viewedUserWageType?: WageType | null;
  /** Optional tab bar to render in header (for Details/Kalender tab switching) */
  tabBar?: React.ReactNode;
  /** Beschäftigungsbeginn des betrachteten Mitarbeiters */
  employmentStart?: string | null;
  /** Beschäftigungsende des betrachteten Mitarbeiters */
  employmentEnd?: string | null;
}

// Fallback-Settings falls getUserSettings fehlschlägt (NI = Firmensitz Niedersachsen)
const DEFAULT_SETTINGS: CompanySettings = {
  vacationDaysFullTime: 30,
  workDaysPerWeekFullTime: 5,
  hoursPerDayFullTime: 8,
  hourlyWage: 0,
  federalState: 'NI',
};

export function TeamMemberCalendar({ userId, userName, year, entries, onBack, onRefresh, canEditEntries = false, onYearChange, previousYearEntries, viewedUserWageType, tabBar, employmentStart, employmentEnd }: Props) {
  const { user } = useAuth();
  const { absenceTypes, vacationTypes, getById: getAbsenceTypeById } = useAbsenceTypes();
  const { saveUserEntries, addManagerNote, getUserSettings, getUserCarryOver, getVacationAdjustments, getUserSettingsSegments } = useAPIStorage();
  const [editingDate, setEditingDate] = useState<Date | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [localEntries, setLocalEntries] = useState<Record<string, DayEntry>>(entries);
  const [vacationRangeOpen, setVacationRangeOpen] = useState(false);
  const [vacationRangeInitialDate, setVacationRangeInitialDate] = useState<string | undefined>();

  // Aktualisiere lokale Einträge wenn props sich ändern
  useEffect(() => {
    setLocalEntries(entries);
  }, [entries]);

  // Urlaubs-KPI: Settings/Carryover/Adjustments laden (nur bei User/Jahr-Wechsel)
  const [vacationBase, setVacationBase] = useState<{
    settings: CompanySettings;
    carryOver: VacationCarryOver | null;
    adjustmentDays: number;
    settingsSegments: SettingsSegment[];
  } | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const [settings, carryOver, adjustments, settingsSegments] = await Promise.all([
          getUserSettings(userId),
          getUserCarryOver(userId, year),
          getVacationAdjustments(userId, year).catch(() => []),
          getUserSettingsSegments(userId, year).catch(() => []),
        ]);
        if (cancelled) return;
        const adjustmentSum = (adjustments ?? []).reduce((s: number, a: { days: number }) => s + a.days, 0);
        setVacationBase({
          settings: settings || DEFAULT_SETTINGS,
          carryOver,
          adjustmentDays: adjustmentSum,
          settingsSegments: settingsSegments ?? [],
        });
      } catch (err) {
        // Fehler beim Laden der VacationKPI-Daten — silent fail
        void err;
      }
    }
    void load();
    return () => { cancelled = true; };
  }, [userId, year, getUserSettings, getUserCarryOver, getVacationAdjustments, getUserSettingsSegments]);

  // Urlaubs-KPI: Berechnung reaktiv bei Entry-Änderungen (ohne neue API-Calls)
  const vacationKPI = useMemo(() => {
    if (!vacationBase) return null;
    const results = calculateResults(
      localEntries, vacationBase.settings, year, new Date(),
      vacationBase.carryOver?.days ?? 0, previousYearEntries, vacationBase.adjustmentDays,
      employmentStart, employmentEnd,
      vacationBase.settingsSegments.length > 0 ? vacationBase.settingsSegments : undefined
    );
    return { results, carryOver: vacationBase.carryOver, adjustmentDays: vacationBase.adjustmentDays };
  }, [vacationBase, localEntries, year, previousYearEntries, employmentStart, employmentEnd]);

  // Feiertage aus den Entries extrahieren
  const holidayDates = useMemo(() => {
    return Object.values(localEntries)
      .filter((e) => e.isHoliday)
      .map((e) => e.date);
  }, [localEntries]);

  const handleSaveManagerNote = async (date: string, note: string) => {
    if (!user) return;

    setIsSaving(true);
    try {
      await addManagerNote(userId, date, note);

      // Lokalen State aktualisieren
      const updatedEntries = { ...localEntries };
      const entry = updatedEntries[date] || {
        date,
        hours: 0,
        isHoliday: false,
        isVacation: false,
      };

      if (note) {
        updatedEntries[date] = {
          ...entry,
          managerNote: note,
          managerNoteAuthorId: user.id,
          managerNoteAuthorName: user.name,
          managerNoteTimestamp: new Date().toISOString(),
        };
      } else {
        const { managerNote: _, managerNoteAuthorId: _a, managerNoteAuthorName: _b, managerNoteTimestamp: _c, ...rest } = entry;
        updatedEntries[date] = rest as DayEntry;
      }

      setLocalEntries(updatedEntries);
      onRefresh();
    } catch (error) {
      console.error('Fehler beim Speichern der Manager-Anmerkung:', error);
      alert('Fehler beim Speichern der Anmerkung');
    } finally {
      setIsSaving(false);
    }
  };

  // Handler für vollständige Entry-Bearbeitung (HR)
  const handleSaveEntry = async (entry: DayEntry) => {
    setIsSaving(true);
    try {
      const updatedEntries = { ...localEntries, [entry.date]: entry };
      await saveUserEntries(userId, year, updatedEntries);
      setLocalEntries(updatedEntries);
      setEditingDate(null);
      onRefresh();
    } catch (error) {
      console.error('Fehler beim Speichern des Eintrags:', error);
      alert('Fehler beim Speichern des Eintrags');
    } finally {
      setIsSaving(false);
    }
  };

  // Handler für Urlaubsblock-Speicherung (HR)
  const handleVacationRangeSave = async (updatedEntries: Record<string, DayEntry>) => {
    setIsSaving(true);
    try {
      await saveUserEntries(userId, year, updatedEntries);
      setLocalEntries(updatedEntries);
      onRefresh();
    } catch (error) {
      console.error('Fehler beim Speichern des Urlaubsblocks:', error);
      alert('Fehler beim Speichern des Urlaubsblocks');
    } finally {
      setIsSaving(false);
    }
  };

  // Urlaubsblock-Dialog öffnen
  const handleOpenVacationRange = (initialDate: string) => {
    setEditingDate(null);
    setVacationRangeInitialDate(initialDate);
    setVacationRangeOpen(true);
  };

  // Kalender-Daten über die shared Utility-Funktion aufbauen
  const calendarMonths = useMemo(
    () => buildCalendarMonths(year, localEntries).months,
    [year, localEntries]
  );

  // Dynamische Neuberechnung der Urlaubsstunden für zukünftige Tage (§11 BUrlG)
  const vacationRecalcMap = useMemo(
    () => recalcFutureVacationHours(localEntries, previousYearEntries),
    [localEntries, previousYearEntries]
  );

  // PLANNED-Absences des betrachteten Mitarbeiters laden + Overlay-Map bauen.
  const [plannedRequests, setPlannedRequests] = useState<AbsenceRequest[]>([]);
  useEffect(() => {
    const storage = new APIStorage();
    let cancelled = false;
    storage
      .getAllAbsenceRequests({
        userId,
        status: 'PLANNED',
        from: `${year}-01-01`,
        to: `${year}-12-31`,
      })
      .then((data) => { if (!cancelled) setPlannedRequests(data); })
      .catch(() => { /* Manager hat ggf. keinen Zugriff — Overlay einfach leer lassen */ });
    return () => { cancelled = true; };
  }, [userId, year]);

  const plannedAbsenceMap = useMemo(() => {
    const map = new Map<string, { absenceType?: AbsenceType; requestId: string }>();
    for (const req of plannedRequests) {
      const start = new Date(req.startDate);
      const end = new Date(req.endDate);
      const cursor = new Date(start);
      while (cursor <= end) {
        const dateStr = `${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, '0')}-${String(cursor.getDate()).padStart(2, '0')}`;
        const value: { absenceType?: AbsenceType; requestId: string } = { requestId: req.id };
        const at = req.absenceType ?? (req.absenceTypeId ? getAbsenceTypeById(req.absenceTypeId) : undefined);
        if (at) value.absenceType = at;
        map.set(dateStr, value);
        cursor.setDate(cursor.getDate() + 1);
      }
    }
    return map;
  }, [plannedRequests, getAbsenceTypeById]);

  // Handler für Klick auf einen Tag (akzeptiert CalendarDay von MonthView/DayCell)
  const handleDayClick = (day: CalendarDay) => {
    // Beide Modi öffnen DayEditModal (Manager: readOnly, HR: vollständig)
    setEditingDate(new Date(day.dateString));
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card>
        <CardContent className="p-5">
          <button
            onClick={onBack}
            className="flex items-center gap-2 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            Zurück zur Übersicht
          </button>
          <div className="flex items-center gap-4 flex-wrap">
            <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
              Kalender: {userName}
            </h1>
            {tabBar}
            {onYearChange && (
              <div className="flex items-center gap-1 bg-gray-100 dark:bg-gray-800 rounded-lg px-2 py-1">
                <button
                  onClick={() => onYearChange(year - 1)}
                  className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded transition-colors"
                  title="Vorheriges Jahr"
                >
                  <ChevronLeft className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                </button>
                <span className="font-semibold min-w-[4rem] text-center text-gray-900 dark:text-gray-100">
                  {year}
                </span>
                <button
                  onClick={() => onYearChange(year + 1)}
                  className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={year >= new Date().getFullYear()}
                  title="Nächstes Jahr"
                >
                  <ChevronRight className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                </button>
              </div>
            )}
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            {canEditEntries ? 'Einträge bearbeiten (HR-Modus)' : 'Manager-Anmerkungen bearbeiten'}
          </p>
          {/* Einzel-Export fuer Manager (HR hat dafuer HRExportPanel) */}
          {!canEditEntries && (
            <div className="mt-3">
              <SingleEmployeeExportButton
                userId={userId}
                userName={userName}
                year={year}
                currentYearEntries={localEntries}
              />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Urlaubs-KPIs */}
      {vacationKPI && (
        <VacationKPICards
          earnedToDate={vacationKPI.results.vacationEarnedToDate ?? 0}
          totalEntitlement={vacationKPI.results.totalVacationEntitlement}
          vacationDaysTaken={vacationKPI.results.vacationDaysTaken}
          remainingVacation={vacationKPI.results.remainingVacation}
          carryOverDays={vacationKPI.results.carryOverFromPreviousYear}
          carryOverExpired={vacationKPI.carryOver?.expired}
          adjustmentDays={vacationKPI.adjustmentDays}
          compact
        />
      )}

      {/* Legende */}
      <Legend absenceTypes={absenceTypes} />

      {/* Kalender-Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {calendarMonths.map((month) => (
          <MonthView
            key={month.month}
            month={month}
            onDayClick={handleDayClick}
            viewedUserWageType={viewedUserWageType}
            vacationRecalcMap={vacationRecalcMap}
            absenceTypeById={getAbsenceTypeById}
            plannedAbsenceMap={plannedAbsenceMap}
          />
        ))}
      </div>

      {/* DayEditModal: HR = vollständig bearbeitbar, Manager = readOnly mit Manager-Note */}
      {editingDate && (
        <DayEditModal
          date={editingDate}
          entry={localEntries[editingDate.toISOString().split('T')[0]!] || null}
          entries={localEntries}
          previousYearEntries={previousYearEntries}
          onSave={handleSaveEntry}
          onClose={() => setEditingDate(null)}
          readOnly={!canEditEntries}
          showManagerNote={!canEditEntries}
          onSaveManagerNote={!canEditEntries ? (note) => {
            const dateStr = editingDate.toISOString().split('T')[0]!;
            void handleSaveManagerNote(dateStr, note);
          } : undefined}
          onOpenVacationRange={canEditEntries ? handleOpenVacationRange : undefined}
          viewedUserWageType={viewedUserWageType}
          vacationRecalcMap={vacationRecalcMap}
          absenceTypes={absenceTypes}
        />
      )}

      {/* Urlaubsblock-Dialog (nur HR) */}
      {canEditEntries && (
        <VacationRangeDialog
          open={vacationRangeOpen}
          onOpenChange={setVacationRangeOpen}
          initialDate={vacationRangeInitialDate}
          entries={localEntries}
          holidays={holidayDates}
          year={year}
          onSave={handleVacationRangeSave}
          employeeName={userName}
          vacationTypes={vacationTypes}
        />
      )}

      <Dialog open={isSaving}>
        <DialogContent className="max-w-xs [&>button.absolute]:hidden" onInteractOutside={(e) => e.preventDefault()}>
          <div className="flex items-center gap-3">
            <div className="w-5 h-5 border-2 border-gray-900 dark:border-gray-100 border-t-transparent rounded-full animate-spin" />
            <span className="text-gray-700 dark:text-gray-300">Speichere...</span>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}


interface SingleEmployeeExportButtonProps {
  userId: string;
  userName: string;
  year: number;
  currentYearEntries: Record<string, DayEntry>;
}

function todayISO(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function monthStartISO(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
}

function SingleEmployeeExportButton({ userId, userName, year, currentYearEntries }: SingleEmployeeExportButtonProps) {
  const { getUserEntries } = useAPIStorage();
  const [open, setOpen] = useState(false);
  const [from, setFrom] = useState<string>(monthStartISO);
  const [to, setTo] = useState<string>(todayISO);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const rangeValid = from.length === 10 && to.length === 10 && from <= to;

  async function handleExport() {
    setBusy(true);
    setErr(null);
    try {
      const fromDate = parseISODate(from);
      const toDate = parseISODate(to);
      const yearsNeeded: number[] = [];
      for (let y = fromDate.getFullYear(); y <= toDate.getFullYear(); y += 1) yearsNeeded.push(y);

      let merged: Record<string, DayEntry> = {};
      for (const y of yearsNeeded) {
        if (y === year) {
          merged = { ...merged, ...currentYearEntries };
        } else {
          const data = await getUserEntries(userId, y);
          if (data) merged = { ...merged, ...data };
        }
      }
      exportDailyRangeCSV(merged, fromDate, toDate, userName);
      setOpen(false);
    } catch (e) {
      console.error("Manager-Einzel-Export fehlgeschlagen:", e);
      setErr(
        e instanceof Error && /403|forbidden|nicht.*zugeordnet/i.test(e.message)
          ? "Keine Berechtigung."
          : "Fehler beim Export.",
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2">
          <Download className="w-4 h-4" />
          Stundenliste exportieren
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80" align="start">
        <div className="space-y-3">
          <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
            Tagesdetails (CSV)
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label htmlFor="single-export-from" className="text-xs text-gray-600 dark:text-gray-400 mb-1">Von</Label>
              <Input
                id="single-export-from"
                type="date"
                value={from}
                onChange={(e) => setFrom(e.target.value)}
                disabled={busy}
              />
            </div>
            <div>
              <Label htmlFor="single-export-to" className="text-xs text-gray-600 dark:text-gray-400 mb-1">Bis</Label>
              <Input
                id="single-export-to"
                type="date"
                value={to}
                onChange={(e) => setTo(e.target.value)}
                disabled={busy}
              />
            </div>
          </div>
          {err && (
            <p className="text-xs text-red-600 dark:text-red-400">{err}</p>
          )}
          <Button
            onClick={handleExport}
            disabled={busy || !rangeValid}
            size="sm"
            className="w-full gap-2"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            Herunterladen
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
